import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, Shield, LogIn, LogOut, UserPlus, Trash2, KeyRound, AlertTriangle, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

const EVENT_META = {
    login_success:         { label: "Prihlásenie",          color: "text-green-400",  bg: "bg-green-400/10", Icon: LogIn },
    login_failed:          { label: "Zlé heslo",            color: "text-red-400",    bg: "bg-red-400/10",   Icon: AlertTriangle },
    login_blocked:         { label: "Účet zablokovaný",     color: "text-orange-400", bg: "bg-orange-400/10",Icon: Ban },
    login_2fa_pending:     { label: "2FA krok",             color: "text-zinc-400",   bg: "bg-zinc-400/10",  Icon: Shield },
    login_2fa_failed:      { label: "Zlý 2FA kód",          color: "text-red-400",    bg: "bg-red-400/10",   Icon: AlertTriangle },
    "2fa_enabled":         { label: "2FA zapnutá",          color: "text-nova-cyan",  bg: "bg-nova-cyan/10", Icon: Shield },
    "2fa_disabled":        { label: "2FA vypnutá",          color: "text-zinc-400",   bg: "bg-zinc-400/10",  Icon: Shield },
    password_changed:      { label: "Heslo zmenené",        color: "text-nova-cyan",  bg: "bg-nova-cyan/10", Icon: KeyRound },
    user_created:          { label: "Používateľ vytvorený", color: "text-green-400",  bg: "bg-green-400/10", Icon: UserPlus },
    user_deleted:          { label: "Používateľ zmazaný",   color: "text-red-400",    bg: "bg-red-400/10",   Icon: Trash2 },
    user_password_changed: { label: "Heslo zmenené (admin)",color: "text-nova-cyan",  bg: "bg-nova-cyan/10", Icon: KeyRound },
    user_role_changed:     { label: "Rola zmenená",         color: "text-nova-purple",bg: "bg-nova-purple/10",Icon: Shield },
};

function EventBadge({ type }) {
    const meta = EVENT_META[type] || { label: type, color: "text-zinc-400", bg: "bg-zinc-400/10", Icon: Shield };
    const { label, color, bg, Icon } = meta;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider ${bg} ${color}`}>
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
}

function formatTs(ts) {
    if (!ts) return "—";
    try {
        return new Date(ts).toLocaleString("sk-SK", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch { return ts; }
}

const PAGE_SIZE = 25;

export default function AdminAuditLog() {
    const [items, setItems]   = useState([]);
    const [total, setTotal]   = useState(0);
    const [page, setPage]     = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback((p = page) => {
        setLoading(true);
        api.getAuditLog(p * PAGE_SIZE, PAGE_SIZE)
            .then(d => { setItems(d.items); setTotal(d.total); })
            .catch(err => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [page]);

    useEffect(() => { load(page); }, [page]);

    const pages = Math.ceil(total / PAGE_SIZE);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Audit Log</h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Prihlásenia, zmeny hesiel, správa používateľov. Celkom: <span className="text-white">{total}</span> záznamov.
                    </p>
                </div>
                <button
                    onClick={() => load(page)}
                    disabled={loading}
                    className="p-2.5 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 transition-colors disabled:opacity-50"
                    title="Obnoviť"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl overflow-hidden">
                {loading && items.length === 0 ? (
                    <div className="space-y-px animate-pulse p-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-14 bg-nova-surface/40 rounded-lg mb-1" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="py-16 text-center text-zinc-600 text-sm">Žiadne záznamy</div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {items.map(item => (
                            <div key={item.id} className="flex items-start gap-4 px-5 py-3 hover:bg-white/2 transition-colors">
                                <div className="shrink-0 w-36 pt-0.5">
                                    <div className="text-xs text-zinc-500 font-mono leading-tight">{formatTs(item.timestamp)}</div>
                                </div>
                                <div className="shrink-0">
                                    <EventBadge type={item.type} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-white text-sm font-medium">{item.username}</span>
                                        {item.target && (
                                            <span className="text-zinc-500 text-xs">→ {item.target}</span>
                                        )}
                                        {item.role && (
                                            <span className="text-nova-purple text-xs font-mono">({item.role})</span>
                                        )}
                                        {item.method && (
                                            <span className="text-zinc-600 text-xs font-mono">[{item.method}]</span>
                                        )}
                                    </div>
                                    {item.ip && (
                                        <div className="text-zinc-600 text-xs font-mono mt-0.5 truncate">
                                            {item.ip}
                                            {item.user_agent && <span className="ml-2 opacity-60">{item.user_agent.substring(0, 60)}{item.user_agent.length > 60 ? "…" : ""}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {pages > 1 && (
                <div className="flex items-center justify-between">
                    <span className="text-zinc-600 text-xs font-mono">Strana {page + 1} z {pages}</span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white disabled:opacity-40 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(pages - 1, p + 1))}
                            disabled={page >= pages - 1}
                            className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white disabled:opacity-40 transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
